import { MessageService } from 'primeng/api';
import { BdExportAsFileDialogResult } from './dialog/dialog.component';
import { BdExportAsFileFormConfig } from './export.config';

/**
 * The two pieces of export chrome every trigger needs and none of them owns:
 * the terminal toast, and the label that names the offered formats.
 *
 * Both used to be private to a trigger component — the toast to
 * `bd-export-as-file`, the label to `bd-chart-export-menu` — which is why a
 * third trigger (the Klinik-Cube's tile menu, BDA-5923) had no way to reuse
 * either without copying the German. Copied wording drifts: a menu item
 * reading "Excel Export" while the dialog offers CSV as well is a bug nobody
 * would think to look for.
 */

/**
 * Raises the terminal success/error toast for a finished export.
 *
 * Takes the caller's own {@link MessageService} rather than injecting one:
 * a trigger inside a dialog and a trigger in a page header sit under
 * different toast hosts, and which host shows the message is the caller's
 * decision.
 */
export function bdExportAsFileNotify(
  messageService: MessageService,
  result: BdExportAsFileDialogResult,
): void {
  if (result.status === 'ok') {
    messageService.add({
      severity: 'success',
      summary: 'Export erstellt',
      detail: result.savedInDownloadCenter
        ? 'Der Export wurde heruntergeladen und im File-Center abgelegt.'
        : 'Der Export wurde heruntergeladen.',
    });
    return;
  }

  messageService.add({
    severity: 'error',
    summary: 'Export fehlgeschlagen',
    detail:
      'Der Export konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.',
  });
}

/**
 * Names the formats an export offers, for a MENU ITEM's label.
 *
 * The styleguide's wording for a table export item is "CSV/Excel Export"
 * (never "Export", "Export (CSV)" or "Excel-Export"), and the item has to say
 * what it produces — so an xlsx-only export is "Excel Export" rather than
 * claiming a CSV it will not write. The export BUTTON's own vocabulary stays
 * "Export"; only the menu item is named this way.
 */
export function bdExportAsFileTypesLabel(
  form: BdExportAsFileFormConfig,
): string {
  const types = form.availableExportTypes;
  const hasCsv = types.includes('csv');
  const hasXlsx = types.includes('xlsx');

  if (hasCsv && !hasXlsx) return 'CSV Export';
  if (hasXlsx && !hasCsv) return 'Excel Export';
  return 'CSV/Excel Export';
}
